import React from "react";
import { Link } from "react-router-dom";
import { Zap, Check, ArrowRight, BarChart3, CreditCard, TrendingUp, Bell, FileText, Link2, Target, Shield, Brain } from "lucide-react";
import RevealOnScroll from "@/components/ui/RevelOnScroll";
import SleekButton from "@/components/ui/NavButtons";
import { SEO } from "@/components/common/SEO";

const APP_URL = "https://app.vawena.ai";

const FEATURES = [
  { icon: Target, title: "Investment Readiness Score", desc: "Get a single AI-powered score that tells you exactly whether you're ready to pursue your next deal — no guessing, no lender surprises." },
  { icon: BarChart3, title: "Net Worth Dashboard", desc: "See your complete financial picture in one place. Assets, liabilities, equity — updated every time you add data. No more manual spreadsheet math." },
  { icon: Target, title: "Buying Power Calculator", desc: "Know your exact capital capacity before you make an offer. Stop losing deals because you weren't sure if you could qualify." },
  { icon: CreditCard, title: "Debt-to-Income Analysis", desc: "Instantly understand your DTI — the number lenders use first. Know if you'll qualify before you ever walk into a bank." },
  { icon: TrendingUp, title: "Monthly Cash Flow Tracking", desc: "See income vs. expenses with trend analysis. Know if you're building toward your next investment or drifting away from it." },
  { icon: Link2, title: "Bank Sync via Plaid", desc: "Connect your accounts once and your numbers update automatically. No manual entry, no stale data, no spreadsheet uploads." },
  { icon: Bell, title: "Smart Milestone Alerts", desc: "Get notified when your net worth hits a new milestone, your DTI approaches a risk zone, or your buying power shifts enough to matter." },
  { icon: FileText, title: "Lender-Ready PDF Reports", desc: "Export clean financial summaries in seconds. Share with mortgage brokers or financial advisors without building decks from scratch." },
  { icon: Brain, title: "AI-Powered Recommendations", desc: "Get personalized guidance — when to pay down debt, when you're ready to invest, and what's actually holding back your buying power." },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Connect your accounts",
    outcome: "Your complete picture in 5 minutes",
    desc: "Link your bank accounts via Plaid or manually enter assets and liabilities. Most investors are fully set up in under 5 minutes.",
  },
  {
    step: "02",
    title: "See your readiness score",
    outcome: "Know your number before a lender does",
    desc: "VaWeNa instantly calculates your net worth, DTI ratio, buying power, and investment readiness score. No spreadsheets. No finance degree.",
  },
  {
    step: "03",
    title: "Track your path to your next deal",
    outcome: "Confident decisions every month",
    desc: "Monitor how your financial position changes over time. Get AI alerts when you hit readiness milestones or when something needs attention.",
  },
];

const PLAN_FEATURES = [
  "Investment readiness score",
  "Net worth dashboard & tracking",
  "Buying power & investment capacity",
  "Debt-to-income (DTI) analysis",
  "Monthly cash flow breakdown",
  "Bank account sync via Plaid",
  "Investment milestone tracker",
  "AI-powered financial insights",
  "Automated alerts & notifications",
  "Financial reports (PDF)",
];

const schema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "WealthScope by VaWeNa",
  "applicationCategory": "FinanceApplication",
  "description": "Track net worth, buying power, debt-to-income ratio, and investment readiness for real estate investors.",
  "offers": { "@type": "Offer", "price": "9.99", "priceCurrency": "USD" },
  "url": "https://vawena.ai/wealthscope",
};

const WealthScopePage: React.FC = () => {
  return (
    <>
      <SEO
        title="WealthScope — Know When You're Ready to Invest in Real Estate"
        description="WealthScope tracks your net worth, buying power, debt-to-income ratio, and deal readiness score so you know exactly when you're financially ready to invest. 14-day free trial."
        canonical="/wealthscope"
        schema={schema}
      />

      <div className="min-h-screen bg-vw-bg">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className="relative pt-20 pb-24 px-4 sm:px-6 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[450px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-[1000px] mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
              <Zap className="w-4 h-4" />
              WealthScope by VaWeNa
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5">
              Know If You Can Afford Your Next{" "}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Investment</span>
              {" "}— Before You Ask a Lender
            </h1>

            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-4 leading-relaxed">
              WealthScope calculates your net worth, buying power, and deal readiness score automatically — so you walk into every conversation with a lender already knowing your numbers. No finance degree required.
            </p>

            {/* Audience callout */}
            <p className="text-sm text-blue-400 font-semibold mb-8">
              Built for investors buying their first or next rental property.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SleekButton variant="primary" onClick={() => window.open(`${APP_URL}/signup`, "_self")}>
                Check My Investment Readiness — Free
              </SleekButton>
              <Link to="/pricing">
                <SleekButton variant="light">View Pricing →</SleekButton>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Credit card required · We'll remind you 3 days before you're charged · Cancel anytime</p>
          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 border-y border-white/5">
            <div className="max-w-[1000px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {[
                { value: "14 days", label: "free trial — no credit card" },
                { value: "< 5 min", label: "to see your readiness score" },
                { value: "10+", label: "financial metrics tracked" },
                { value: "17%", label: "savings on annual plan" },
              ].map(({ value, label }) => (
                <div key={label}>
                  <p className="text-3xl font-extrabold text-white mb-1">{value}</p>
                  <p className="text-sm text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Who it's for ─────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-16 px-4 sm:px-6">
            <div className="max-w-[900px] mx-auto">
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  {
                    title: "Pre-first investment",
                    desc: "You're saving toward your first rental and want to know when your numbers actually support buying — not just when you feel ready.",
                  },
                  {
                    title: "Between investments",
                    desc: "You own 1–2 properties and want to know if you can qualify for your next acquisition without getting a surprise from a lender.",
                    highlight: true,
                  },
                  {
                    title: "Growing your portfolio",
                    desc: "You're scaling and need ongoing visibility into DTI, buying power, and net worth as each new deal changes your financial position.",
                  },
                ].map(({ title, desc, highlight }) => (
                  <div
                    key={title}
                    className={`rounded-2xl p-5 border ${
                      highlight
                        ? "bg-blue-500/10 border-blue-500/25"
                        : "bg-white/[0.02] border-white/10"
                    }`}
                  >
                    {highlight && (
                      <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-blue-400 bg-blue-500/15 px-2 py-0.5 rounded-full mb-3">Most common</span>
                    )}
                    <h3 className="text-white font-bold text-sm mb-2">{title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Features ─────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6">
            <div className="max-w-[1000px] mx-auto">
              <div className="text-center mb-12">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">What you get</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                  Stop piecing together your financial picture
                </h2>
                <p className="text-slate-400 max-w-xl mx-auto">
                  Every metric real estate investors need — net worth, buying power, DTI, deal readiness — in one intelligent dashboard that updates automatically.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <div
                    key={title}
                    className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300"
                  >
                    <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                      <Icon className="w-4 h-4 text-blue-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-2 text-sm">{title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── How it works ─────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6 bg-white/[0.01]">
            <div className="max-w-[900px] mx-auto text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">How it works</p>
              <h2 className="text-3xl font-extrabold text-white mb-14">
                From sign-up to investment clarity in under 10 minutes
              </h2>
              <div className="grid sm:grid-cols-3 gap-10">
                {HOW_IT_WORKS.map(({ step, title, outcome, desc }) => (
                  <div key={step}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 text-lg font-extrabold mb-5">
                      {step}
                    </div>
                    <h3 className="text-white font-bold mb-1">{title}</h3>
                    <p className="text-xs text-blue-400 font-semibold mb-2">{outcome}</p>
                    <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Pricing CTA ──────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6">
            <div className="max-w-[560px] mx-auto bg-white/[0.03] border border-white/10 rounded-3xl p-8 sm:p-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
                <Check className="w-3.5 h-3.5" />
                14-day free trial · We'll remind you before you're charged
              </div>
              <h2 className="text-3xl font-extrabold text-white mb-2">WealthScope</h2>
              <div className="flex items-end justify-center gap-1 mb-1">
                <span className="text-5xl font-extrabold text-white">$9.99</span>
                <span className="text-slate-500 mb-1.5">/month</span>
              </div>
              <p className="text-sm text-blue-400 font-semibold mb-7">or $99.90/year — save 17% billed annually</p>
              <ul className="text-left space-y-2.5 mb-8 max-w-xs mx-auto">
                {PLAN_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-blue-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-colors"
              >
                Start Free 14-Day Trial
              </button>
              <p className="mt-4 text-xs text-slate-500">We'll email you 3 days before your trial ends. Cancel anytime.</p>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Trust ────────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 border-t border-white/5">
            <div className="max-w-[900px] mx-auto">
              <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-8">Built with bank-level security</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                {[
                  { icon: Shield, label: "AES-256 encryption at rest" },
                  { icon: Link2, label: "Plaid-secured bank sync" },
                  { icon: Check, label: "Row-level data isolation" },
                  { icon: Shield, label: "Google Cloud Armor WAF" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-400">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Bottom CTA ───────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              Stop guessing. Know your number.
            </h2>
            <p className="text-slate-500 mb-2 max-w-md mx-auto">
              See your investment readiness score, buying power, and DTI — in under 10 minutes. No finance degree. No spreadsheets.
            </p>
            <p className="text-sm text-blue-400 font-semibold mb-8">Free for 14 days. We'll remind you before you're charged.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                className="inline-flex items-center gap-2 px-8 h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
              >
                Check My Readiness — Free <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/portfolioiq">
                <button type="button" className="inline-flex items-center gap-2 px-8 h-12 rounded-full border border-white/10 text-slate-300 font-semibold text-sm hover:bg-white/5 transition-colors">
                  Already have properties? See PortfolioIQ →
                </button>
              </Link>
            </div>
          </section>
        </RevealOnScroll>
      </div>
    </>
  );
};

export default WealthScopePage;
